import React, { useState, useEffect } from "react";
import { Offcanvas, OffcanvasHeader, OffcanvasBody, Input, Nav, NavItem, NavLink, TabContent, TabPane, Badge } from "reactstrap";
import SimpleBar from "simplebar-react";
import classnames from "classnames";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import moment from "moment-timezone";
import "moment/locale/mn";
import Loader from "../../Components/Common/Loader";

moment.updateLocale("mn", {
  relativeTime: {
    future: "%s дараа",
    past: "%s өмнө",
    s: "хэдхэн секундын",
    ss: "%d секундын",
    m: "1 минутын",
    mm: "%d минутын",
    h: "1 цагийн",
    hh: "%d цагийн",
    d: "1 өдрийн",
    dd: "%d өдрийн",
    M: "1 сарын",
    MM: "%d сарын",
    y: "1 жилийн",
    yy: "%d жилийн",
  },
});

import dummyImg from "../../assets/images/users/user-dummy-img.jpg";
import { getUsers as onGetUsers } from "../../slices/thunks";

const ActiveUsers = () => {
  const dispatch = useDispatch();
  const [isRight, setIsRight] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showLoader, setShowLoader] = useState(true);
  const [activeTab, setActiveTab] = useState("1");

  const selectLayoutState = (state) => state.Crm;
  const crmuserData = createSelector(selectLayoutState, (state) => ({
    crmusers: state.crmusers,
    isUserSuccess: state.isUserSuccess,
    error: state.error,
  }));

  const { crmusers, isUserSuccess, error } = useSelector(crmuserData);

  useEffect(() => {
    dispatch(onGetUsers());
    const interval = setInterval(() => {
      dispatch(onGetUsers());
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 90000);

    return () => clearTimeout(timer);
  }, []);

  const toggleRightCanvas = () => setIsRight(!isRight);

  const isOnline = (lastActive) => {
    if (!lastActive) return false;
    const diff = (new Date() - new Date(lastActive)) / 1000 / 60;
    return diff <= 5;
  };

  const formatLastActive = (time) => {
    if (!time) return "Offline";
    return moment.tz(time, "Asia/Ulaanbaatar").fromNow();
  };

  const userList = Array.isArray(crmusers) ? crmusers : [];

  const filteredUsers = userList
    .filter((user) => {
      const name = `${user.first_name} ${user.last_name}`.toLowerCase();
      const company = (user.company || "").toLowerCase();
      const term = searchTerm.toLowerCase();
      return name.includes(term) || company.includes(term);
    })
    .sort((a, b) => isOnline(b.lastActive) - isOnline(a.lastActive));

  const internalUsers = filteredUsers.filter(user => user.company === "TILogistic");
  const externalUsers = filteredUsers.filter(user => user.company !== "TILogistic");

  const renderUsers = (usersToRender) => {
    if (!usersToRender || usersToRender.length === 0) {
      return (
        <div className="text-center text-muted mt-5 pt-3">
          <i className="ri-search-line display-4 text-muted border border-dashed rounded-circle p-3"></i>
          <p className="mt-3 fs-15">Хайлтанд тохирох хэрэглэгч олдсонгүй</p>
        </div>
      );
    }

    return (
      <div className="acitivity-timeline p-3">
        {usersToRender.map((user) => {
          const online = isOnline(user.lastActive);
          return (
            <div
              key={user._id}
              className="acitivity-item d-flex align-items-center mb-3 p-2 rounded shadow-sm"
              style={{
                background: online ? "#e6ffed" : "#f8f9fa",
                borderLeft: online ? "4px solid #4caf50" : "4px solid #ccc",
                transition: "all 0.3s ease",
              }}
            >
              <div className="flex-shrink-0">
                <img
                  src={user.image_url || dummyImg}
                  alt={user.first_name}
                  className="avatar-xs rounded-circle"
                  style={{
                    border: online ? "2px solid #4caf50" : "2px solid #ccc",
                    objectFit: "cover",
                  }}
                  onError={(e) => { e.target.onerror = null; e.target.src = dummyImg; }}
                />
              </div>
              <div className="flex-grow-1 ms-3">
                <h6 className="mb-1 d-flex align-items-center">
                  {formatName(user)}{" "}
                  {online && (
                    <span
                      className="ms-2"
                      style={{
                        height: "10px",
                        width: "10px",
                        backgroundColor: "#4caf50",
                        borderRadius: "50%",
                        display: "inline-block",
                        boxShadow: "0 0 5px #4caf50",
                      }}
                      title="Online"
                    ></span>
                  )}
                </h6>

                {!online && (
                  <small className="text-muted d-block mb-1 d-flex align-items-center">
                    <i className="ri-time-line me-1"></i>
                    {formatLastActive(user.lastActive)}
                  </small>
                )}

                <small className="text-muted d-block mb-1 d-flex align-items-center text-truncate" style={{ maxWidth: "200px" }}>
                  <i className="ri-building-line me-1"></i>
                  {user.company || "-"}
                </small>

                <small className="text-muted d-block mb-1 d-flex align-items-center text-truncate" style={{ maxWidth: "200px" }}>
                  <i className="ri-suitcase-line me-1"></i>
                  {user.role || "-"}
                </small>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const onlineCount = userList.filter((u) => isOnline(u.lastActive)).length;

  const formatName = (user) => {
    const firstInitial = user.last_name ? user.last_name[0].toUpperCase() : "";
    return `${firstInitial}.${user.first_name}`;
  };

  return (
    <React.Fragment>
      <div className="ms-1 header-item d-none d-sm-flex">
        <button
          onClick={toggleRightCanvas}
          type="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        >
          <i className="ri-team-fill fs-22"></i>
          <span className="position-absolute cartitem-badge topbar-badge fs-10 translate-middle badge rounded-pill bg-primary">
            {onlineCount}
          </span>
        </button>
      </div>

      <Offcanvas
        isOpen={isRight}
        direction="end"
        toggle={toggleRightCanvas}
        id="offcanvasRight"
        className="border-bottom"
      >
        <OffcanvasHeader toggle={toggleRightCanvas} id="offcanvasRightLabel">
          Онлайн хэрэглэгчид
        </OffcanvasHeader>
        <OffcanvasBody className="p-0 overflow-hidden">
          <div className="p-3 border-bottom bg-light">
            <Nav tabs className="nav-tabs-custom nav-success nav-justified mb-3">
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer", padding: "8px 5px", fontSize: "13px" }}
                  className={classnames({ active: activeTab === "1" })}
                  onClick={() => setActiveTab("1")}
                >
                  <i className="ri-building-2-fill me-1 align-bottom"></i>TILogistic
                  <Badge color={activeTab === "1" ? "success" : "light"} className={activeTab === "1" ? "ms-1" : "ms-1 text-muted"}>
                    {internalUsers?.filter(u => isOnline(u.lastActive)).length || 0}
                  </Badge>
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer", padding: "8px 5px", fontSize: "13px" }}
                  className={classnames({ active: activeTab === "2" })}
                  onClick={() => setActiveTab("2")}
                >
                  <i className="ri-team-fill me-1 align-bottom"></i>Бусад
                  <Badge color={activeTab === "2" ? "success" : "light"} className={activeTab === "2" ? "ms-1" : "ms-1 text-muted"}>
                    {externalUsers?.filter(u => isOnline(u.lastActive)).length || 0}
                  </Badge>
                </NavLink>
              </NavItem>
            </Nav>

            <div className="search-box">
              <Input
                type="text"
                placeholder="Хэрэглэгч болон компани хайх..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control rounded-pill bg-white border-light"
              />
              <i className="ri-search-line search-icon"></i>
            </div>
          </div>

          {isUserSuccess ? (
            <SimpleBar style={{ height: "calc(100vh - 145px)" }}>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                  {renderUsers(internalUsers)}
                </TabPane>
                <TabPane tabId="2">
                  {renderUsers(externalUsers)}
                </TabPane>
              </TabContent>
            </SimpleBar>
          ) : !showLoader ? (
            <div className="text-center py-4 text-muted">
              Мэдээлэл байхгүй байна.
            </div>
          ) : (
            <Loader error={error} />
          )}
        </OffcanvasBody>
      </Offcanvas>
    </React.Fragment>
  );
};

export default ActiveUsers;
